import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UserSummary } from '@shared/domain';
import { useAuth } from '@/state/auth';
import { useCreateUser, usePatchUser, useResetUserPassword, useUsers } from '@/lib/query';
import { Badge, Card, DataTable } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';

const ROLES = ['operator', 'supervisor', 'engineer', 'auditor', 'admin'];

export default function Users() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { data: users, error } = useUsers();
  const create = useCreateUser();
  const patch = usePatchUser();
  const reset = useResetUserPassword();
  const [msg, setMsg] = useState<string | null>(null);
  const [form, setForm] = useState({ username: '', displayName: '', role: 'operator', password: '' });

  if (user?.role !== 'admin') {
    return (
      <div>
        <PageBanner />
        <Card className="p-10 text-center text-[14px] text-slate-500">
          仅系统管理员可访问用户管理。 <button onClick={() => nav('/')} className="text-brand-600 hover:underline">返回</button>
        </Card>
      </div>
    );
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    create
      .mutateAsync(form)
      .then(() => { setMsg(`已创建 ${form.username}`); setForm({ username: '', displayName: '', role: 'operator', password: '' }); })
      .catch((err: Error) => setMsg(err.message));
  };
  const doReset = (u: UserSummary) => {
    const pw = window.prompt(`为 ${u.username} 设置新密码（≥8 位）`);
    if (!pw) return;
    reset.mutateAsync({ id: u.id, password: pw }).then(() => setMsg(`已重置 ${u.username} 密码`)).catch((e: Error) => setMsg(e.message));
  };

  return (
    <div>
      <PageBanner />
      <div className="mb-6">
        <h2 className="text-[24px] font-bold text-slate-900">用户管理</h2>
        <p className="mt-1 text-[14px] text-slate-500">账号、角色与状态。停用后该账号无法登录。</p>
      </div>

      {msg ? <div className="mb-4 rounded-lg bg-info-bg px-4 py-2 text-[13px] text-info-fg">{msg}</div> : null}
      {error ? <div className="mb-4 rounded-lg bg-alarm-bg px-4 py-2 text-[13px] text-alarm-fg">{(error as Error).message}</div> : null}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <div className="mb-3 text-[16px] font-semibold text-slate-800">账号列表</div>
          <DataTable
            columns={[
              { key: 'username', header: '用户名', render: (u: UserSummary) => <span className="font-mono text-[13px]">{u.username}</span> },
              { key: 'displayName', header: '姓名' },
              { key: 'role', header: '角色', render: (u: UserSummary) => <Badge>{u.role}</Badge> },
              { key: 'active', header: '状态', render: (u: UserSummary) => (
                <span className="flex flex-wrap items-center gap-2">
                  {u.active ? <Badge className="bg-ok-bg text-ok-fg border-ok-border">启用</Badge> : <Badge className="bg-alarm-bg text-alarm-fg border-alarm-border">停用</Badge>}
                  {u.mustChangePassword && <Badge className="bg-warn-bg text-warn-fg border-warn-border">须改密</Badge>}
                </span>
              ) },
              {
                key: 'ops',
                header: '操作',
                render: (u: UserSummary) => (
                  <div className="flex gap-3">
                    <button onClick={() => patch.mutate({ id: u.id, patch: { active: !u.active } })} disabled={u.id === user?.id} className="text-brand-600 hover:underline disabled:text-slate-300">
                      {u.active ? '停用' : '启用'}
                    </button>
                    <button onClick={() => doReset(u)} className="text-brand-600 hover:underline">重置密码</button>
                  </div>
                ),
              },
            ]}
            rows={users ?? []}
          />
        </Card>

        <Card className="p-6">
          <div className="mb-3 text-[16px] font-semibold text-slate-800">新建账号</div>
          <form onSubmit={submit} className="space-y-3">
            <label className="block text-[13px] text-slate-500">用户名
              <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" />
            </label>
            <label className="block text-[13px] text-slate-500">姓名
              <input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" />
            </label>
            <label className="block text-[13px] text-slate-500">角色
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm">
                {ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
              </select>
            </label>
            <label className="block text-[13px] text-slate-500">初始密码（≥8）
              <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 w-full rounded-lg border border-hairline px-3 py-2 text-sm" />
            </label>
            <button disabled={create.isPending} className="btn btn-primary w-full justify-center disabled:opacity-60">{create.isPending ? '创建中…' : '创建'}</button>
          </form>
        </Card>
      </div>
    </div>
  );
}
